import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { logAssert } from "@faire/web--source/common/logging";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import classNames from "classnames";
import React from "react";
import { useHistory } from "react-router-dom";
import { SlideIn } from "@faire/retailer-visitor-shared/components/SlideIn";
import { popSignUp, SignUpElement, SignUpType } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { getIsRobot } from "@faire/retailer-visitor-shared/serialized-data/getIsRobot";
import { getLocaleKey } from "@faire/retailer-visitor-shared/serialized-data/getLocaleKey";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { useSettingLocalizedHomepage } from "@faire/retailer/settings/useSettingLocalizedHomepage";
import { useAssignSettingLoggedOutHomepageNewTopBannerCopy } from "@faire/retailer/settings/useSettingLoggedOutHomepageNewTopBannerCopy";
import { useSettingLoggedOutHomepageRedesignV2 } from "@faire/retailer/settings/useSettingLoggedOutHomepageRedesignV2";
import { LoggedOutHomeImage } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeImage/LoggedOutHomeImage";
import { useTopHeaderBannerText, useGetTopHeaderBannerVariant } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/TopHeaderBanner/__internal__/useGetTopHeaderBannerVariants";
import * as componentStyles from "./styles.css";
export var TopHeaderBanner = function(param) {
    var loadTimingRef = param.loadTimingRef;
    var _getLocaleKey;
    var isLocalizedExperiment = useSettingLocalizedHomepage();
    var assignSettingLoggedOutHomepageNewTopBannerCopy = useAssignSettingLoggedOutHomepageNewTopBannerCopy();
    var countryCode = getSessionCountry();
    var localeCountry = (_getLocaleKey = getLocaleKey()) === null || _getLocaleKey === void 0 ? void 0 : _getLocaleKey.country;
    var isRobot = getIsRobot();
    var showLocalizedContent = isLocalizedExperiment && (isRobot && (localeCountry === "FRA" || localeCountry === "GBR") || !isRobot && (countryCode === "FRA" || countryCode === "GBR"));
    var currentVariant = useGetTopHeaderBannerVariant({
        showLocalizedContent: showLocalizedContent
    });
    logAssert(!!currentVariant, "currentVariant is hardcoded, cannot be null");
    var backgroundColor = currentVariant.backgroundColor, responsiveImage = currentVariant.responsiveImage, variantId = currentVariant.variantId, name = currentVariant.name, location = currentVariant.location;
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    var ref = useDoActionWhenVisible(function() {
        assignSettingLoggedOutHomepageNewTopBannerCopy();
        trackLoggedOutRetailersLoggedOutHomeRedesignView(variantId !== null && variantId !== void 0 ? variantId : "", "".concat(homepageRedesignVariant));
    });
    var isDesktopAndAbove = useViewport().isDesktopAndAbove;
    return /*#__PURE__*/ _jsx("div", {
        ref: loadTimingRef,
        children: /*#__PURE__*/ _jsx(SlideIn, {
            offset: getSpacing("3x"),
            children: /*#__PURE__*/ _jsxs(Flex, {
                className: "m-4 h-fit md:m-6",
                direction: {
                    mobileAndAbove: "column-reverse",
                    desktopAndAbove: "row"
                },
                style: {
                    backgroundColor: backgroundColor,
                    position: "relative"
                },
                "data-test-id": "top-header-banner",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        ref: ref,
                        className: "w-full text-center lg:w-1/2 lg:text-left",
                        style: {
                            height: "100%",
                            alignItems: "center",
                            display: "flex",
                            position: "relative"
                        },
                        children: /*#__PURE__*/ _jsx(LeftContent, {})
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative w-full lg:w-1/2",
                        children: [
                            /*#__PURE__*/ _jsx(LoggedOutHomeImage, {
                                responsiveImage: responsiveImage,
                                optimizeImageOptions: showLocalizedContent ? {
                                    mobileAndAbove: {
                                        width: 355,
                                        height: 150
                                    },
                                    tabletAndAbove: {
                                        width: 704,
                                        height: 532
                                    },
                                    desktopAndAbove: {
                                        width: 420,
                                        height: 262
                                    },
                                    xLargeAndAbove: {
                                        width: 698,
                                        height: 316
                                    },
                                    xxLargeDesktop: {
                                        width: 842,
                                        height: 372
                                    }
                                } : {
                                    mobileAndAbove: {
                                        width: componentStyles.MOBILE_BANNER_WIDTH,
                                        height: componentStyles.MOBILE_BANNER_HEIGHT
                                    },
                                    tabletAndAbove: {
                                        width: componentStyles.TABLET_BANNER_WIDTH,
                                        height: componentStyles.TABLET_BANNER_HEIGHT
                                    },
                                    desktopAndAbove: {
                                        width: componentStyles.DESKTOP_BANNER_WIDTH,
                                        height: componentStyles.DESKTOP_BANNER_HEIGHT
                                    },
                                    xLargeAndAbove: {
                                        width: componentStyles.XL_DESKTOP_BANNER_WIDTH,
                                        height: componentStyles.XL_DESKTOP_BANNER_HEIGHT
                                    },
                                    xxLargeDesktop: {
                                        width: componentStyles.XXL_DESKTOP_BANNER_WIDTH,
                                        height: componentStyles.XL_DESKTOP_BANNER_HEIGHT
                                    }
                                },
                                imageStyles: isDesktopAndAbove ? {
                                    display: "inline",
                                    height: 0,
                                    minHeight: "100%",
                                    objectPosition: "center 25%"
                                } : {}
                            }),
                            /*#__PURE__*/ _jsx(Credit, {
                                name: name,
                                location: location
                            })
                        ]
                    })
                ]
            })
        })
    });
};
var LeftContent = function() {
    var headerText = useTopHeaderBannerText();
    var history = useHistory();
    return /*#__PURE__*/ _jsxs(Column, {
        align: {
            mobileAndAbove: "center",
            desktopAndAbove: "flex-start"
        },
        className: classNames("mx-auto", "px-4 py-10", "md:px-20 md:py-16", "lg:p-14", "xl:px-28 xl:py-20", "2xl:px-[168px] 2xl:py-28"),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                as: "h1",
                variant: {
                    mobileAndAbove: "displayMSerifRegular",
                    tabletAndAbove: "displayLSerifRegular"
                },
                color: Core.text.inverse,
                children: headerText
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "4x"
            }),
            /*#__PURE__*/ _jsx(Button, {
                style: {
                    width: "fit-content",
                    border: "none"
                },
                variant: "secondary",
                onClick: function() {
                    return popSignUp(history, SignUpType.GENERIC, function() {
                        return null;
                    }, {
                        element: SignUpElement.TOP_BANNER,
                        sourceId: "logged_out_homepage_top_header_banner"
                    });
                },
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "9ZsWHF",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sign up for free"
                        }
                    ]
                })
            })
        ]
    });
};
var Credit = function(param) {
    var name = param.name, location = param.location;
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        desktopAndAbove: /*#__PURE__*/ _jsxs("div", {
            className: "[text-shadow:1px_1px_4px_rgba(0,0,0,0.25) absolute bottom-8 right-8 flex flex-col text-right",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansMedium",
                    color: Core.text.inverse,
                    children: name
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    color: Core.text.inverse,
                    children: location
                })
            ]
        })
    });
};
